import React, { useState } from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import Image from './Image';
import { getBrandColor, getBrandInitial, isValidImageUrl } from '~src/utils/brandAvatarGenerator';

interface BrandAvatarProps {
  brandName: string;
  profileImage?: string | null;
  size?: number;
  sx?: SxProps<Theme>;
  fontSize?: string | number;
  'data-testid'?: string;
}

/**
 * BrandAvatar component that displays either the brand's profile image
 * or a generated avatar with the brand's initial and color
 */
export const BrandAvatar: React.FC<BrandAvatarProps> = ({
  brandName,
  profileImage,
  size = 96,
  sx = {},
  fontSize,
  'data-testid': dataTestId,
}) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = isValidImageUrl(profileImage) && !imageError;
  
  const brandColor = getBrandColor(brandName);
  const brandInitial = getBrandInitial(brandName);
  
  // Calculate font size based on avatar size if not provided
  const calculatedFontSize = fontSize || `${size * 0.4}px`;

  if (hasValidImage) {
    return (
      <Box
        data-testid={dataTestId}
        sx={{
          borderRadius: '50%',
          width: size,
          height: size,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
      >
        <Image
          src={profileImage!}
          alt={`${brandName} profile`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setImageError(true)}
        />
      </Box>
    );
  }

  // Fallback to generated avatar
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        borderRadius: '50%',
        width: size,
        height: size,
        backgroundColor: brandColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        userSelect: 'none',
        ...sx,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize: calculatedFontSize,
          fontWeight: 600,
          color: 'white',
          lineHeight: 1,
        }}
      >
        {brandInitial}
      </Typography>
    </Box>
  );
};

export default BrandAvatar; 